import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Building2, DollarSign, FlaskConical, KeyRound, LogOut, Menu, Plus, Target } from "lucide-react";
import type { Admin, Dealership } from "@lumika/shared";
import {
  Badge,
  Button,
  MobileAppBar,
  SearchInput,
  Sheet,
  SheetBody,
  SheetContent,
  SheetTitle,
  toast,
} from "@lumika/ui";
import { adminApi } from "../api/admin";
import { LumikaLogo } from "../components/LumikaLogo";

const STATUS_VARIANT: Record<Dealership["status"], "default" | "success" | "warning" | "secondary"> = {
  onboarding: "warning",
  pilot: "warning",
  active: "success",
  paused: "secondary",
  inactive: "secondary",
};

export function AdminShell({ admin }: { admin: Admin }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Debounce the sidebar search 300ms so each keystroke doesn't fire a
  // request; placeholderData keeps the previous list visible while the
  // next result loads.
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);
  const dealershipsQuery = useQuery({
    queryKey: ["dealerships", debouncedSearch.trim()],
    queryFn: () => adminApi.listDealerships(debouncedSearch),
    placeholderData: (prev) => prev,
  });

  // Drives the "due for follow-up" count badge on the Outreach tab.
  const outreachSummaryQuery = useQuery({
    queryKey: ["outreach", "summary"],
    queryFn: adminApi.outreachSummary,
  });
  const dueCount = outreachSummaryQuery.data?.dueCount ?? 0;

  const dealerships = dealershipsQuery.data ?? [];
  const trimmed = search.trim();
  const adminName = admin.displayName ?? admin.email;
  const activeDealer = dealerships.find((d) => location.pathname.includes(`/dealerships/${d.id}`));

  // Close the drawer on route change.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Shared by the desktop sidebar and mobile drawer sign-out buttons.
  // Only redirect once the logout actually succeeded — a swallowed
  // failure would leave the session cookie valid with zero feedback.
  const signOut = async () => {
    try {
      await adminApi.logout();
    } catch {
      toast.error("Couldn't sign out — try again.");
      return;
    }
    window.location.assign("/sign-in");
  };

  const drawerBody = (
    <>
      <div className="px-3 pt-1">
        <Button
          type="button"
          onClick={() => navigate("/dealerships/new")}
          className="w-full justify-center"
        >
          <Plus className="h-4 w-4" />
          New dealership
        </Button>
      </div>
      <div className="px-3">
        <SearchInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or slug"
          aria-label="Search dealerships"
        />
      </div>
      <nav aria-label="Dealerships" className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto px-3 pb-3">
        {dealershipsQuery.isLoading ? (
          <p className="px-3 py-4 text-xs text-muted-foreground">Loading…</p>
        ) : dealerships.length === 0 ? (
          <div className="px-3 py-6 text-center text-xs text-muted-foreground">
            {trimmed ? `No dealerships match “${trimmed}”.` : "No dealerships yet."}
          </div>
        ) : (
          dealerships.map((dealer) => (
            <NavLink
              key={dealer.id}
              to={`/dealerships/${dealer.id}`}
              className={({ isActive }) =>
                [
                  "group flex min-h-touch items-center justify-between gap-2 rounded-md px-3 py-2 text-sm transition-colors",
                  isActive
                    ? "bg-accent text-accent-foreground"
                    : "hover:bg-muted",
                ].join(" ")
              }
            >
              <span className="min-w-0 truncate font-medium">{dealer.name}</span>
              <Badge variant={STATUS_VARIANT[dealer.status]} className="shrink-0">
                {dealer.status}
              </Badge>
            </NavLink>
          ))
        )}
      </nav>
      {/* Top-level admin sections — mirrors the desktop sidebar so
          mobile admins can reach SFTP and Platform billing without
          drilling into a dealership first. */}
      <nav aria-label="Admin sections" className="flex flex-col gap-0.5 border-t px-3 pt-3 pb-1">
        <NavLink
          to="/outreach"
          className={({ isActive }) =>
            [
              "flex min-h-touch items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors",
              isActive
                ? "bg-accent text-accent-foreground"
                : "text-muted-foreground hover:bg-muted hover:text-foreground",
            ].join(" ")
          }
        >
          <Target className="h-4 w-4" />
          <span className="font-medium">Outreach</span>
          {dueCount > 0 && (
            <Badge variant="warning" className="ml-auto shrink-0">
              {dueCount}
            </Badge>
          )}
        </NavLink>
        <NavLink
          to="/sftp"
          className={({ isActive }) =>
            [
              "flex min-h-touch items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors",
              isActive
                ? "bg-accent text-accent-foreground"
                : "text-muted-foreground hover:bg-muted hover:text-foreground",
            ].join(" ")
          }
        >
          <KeyRound className="h-4 w-4" />
          <span className="font-medium">SFTP</span>
        </NavLink>
        <NavLink
          to="/channels-playground"
          className={({ isActive }) =>
            [
              "flex min-h-touch items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors",
              isActive
                ? "bg-accent text-accent-foreground"
                : "text-muted-foreground hover:bg-muted hover:text-foreground",
            ].join(" ")
          }
        >
          <FlaskConical className="h-4 w-4" />
          <span className="font-medium">Channels Playground</span>
        </NavLink>
        <NavLink
          to="/billing"
          className={({ isActive }) =>
            [
              "flex min-h-touch items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors",
              isActive
                ? "bg-accent text-accent-foreground"
                : "text-muted-foreground hover:bg-muted hover:text-foreground",
            ].join(" ")
          }
        >
          <DollarSign className="h-4 w-4" />
          <span className="font-medium">Platform billing</span>
        </NavLink>
      </nav>
    </>
  );

  return (
    <div className="flex h-dvh flex-col bg-background text-foreground md:grid md:h-dvh md:overflow-hidden md:grid-cols-[280px_minmax(0,1fr)]">
      {/* Mobile top bar */}
      <MobileAppBar
        leading={
          <Button
            variant="ghost"
            size="icon"
            aria-label="Open dealership list"
            onClick={() => setDrawerOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
        }
        title={activeDealer?.name ?? "lumika admin"}
        trailing={
          <Button
            variant="ghost"
            size="icon"
            aria-label="New dealership"
            onClick={() => navigate("/dealerships/new")}
          >
            <Plus className="h-5 w-5" />
          </Button>
        }
      />

      <aside className="hidden flex-col gap-5 border-r bg-card p-4 md:flex md:h-dvh md:overflow-hidden">
        <header className="flex items-center justify-between">
          <Link to="/dealerships" className="flex items-center gap-2 font-semibold tracking-tight">
            <LumikaLogo theme="auto" size={28} />
            <span>lumika admin</span>
          </Link>
        </header>
        <Button
          type="button"
          onClick={() => navigate("/dealerships/new")}
          className="justify-center"
        >
          <Plus className="h-4 w-4" />
          New dealership
        </Button>
        <SearchInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or slug"
          aria-label="Search dealerships"
        />
        <nav aria-label="Dealerships" className="-mr-2 flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto pr-1">
          {dealershipsQuery.isLoading ? (
            <p className="px-3 py-4 text-xs text-muted-foreground">Loading…</p>
          ) : dealerships.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-muted-foreground">
              {trimmed ? `No dealerships match “${trimmed}”.` : "No dealerships yet."}
            </div>
          ) : (
            dealerships.map((dealer) => (
              <NavLink
                key={dealer.id}
                to={`/dealerships/${dealer.id}`}
                className={({ isActive }) =>
                  [
                    "group flex items-center justify-between gap-2 rounded-md px-3 py-2 text-sm transition-colors",
                    isActive
                      ? "bg-accent text-accent-foreground"
                      : "hover:bg-muted",
                  ].join(" ")
                }
              >
                <span className="min-w-0 truncate font-medium">{dealer.name}</span>
                <Badge variant={STATUS_VARIANT[dealer.status]} className="shrink-0">
                  {dealer.status}
                </Badge>
              </NavLink>
            ))
          )}
        </nav>
        <NavLink
          to="/outreach"
          className={({ isActive }) =>
            [
              "flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors",
              isActive
                ? "bg-accent text-accent-foreground"
                : "text-muted-foreground hover:bg-muted hover:text-foreground",
            ].join(" ")
          }
        >
          <Target className="h-4 w-4" />
          <span className="font-medium">Outreach</span>
          {dueCount > 0 && (
            <Badge variant="warning" className="ml-auto shrink-0">
              {dueCount}
            </Badge>
          )}
        </NavLink>
        <NavLink
          to="/sftp"
          className={({ isActive }) =>
            [
              "flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors",
              isActive
                ? "bg-accent text-accent-foreground"
                : "text-muted-foreground hover:bg-muted hover:text-foreground",
            ].join(" ")
          }
        >
          <KeyRound className="h-4 w-4" />
          <span className="font-medium">SFTP</span>
        </NavLink>
        <NavLink
          to="/channels-playground"
          className={({ isActive }) =>
            [
              "flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors",
              isActive
                ? "bg-accent text-accent-foreground"
                : "text-muted-foreground hover:bg-muted hover:text-foreground",
            ].join(" ")
          }
        >
          <FlaskConical className="h-4 w-4" />
          <span className="font-medium">Channels Playground</span>
        </NavLink>
        <NavLink
          to="/billing"
          className={({ isActive }) =>
            [
              "flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors",
              isActive
                ? "bg-accent text-accent-foreground"
                : "text-muted-foreground hover:bg-muted hover:text-foreground",
            ].join(" ")
          }
        >
          <DollarSign className="h-4 w-4" />
          <span className="font-medium">Platform billing</span>
        </NavLink>
        <footer className="flex items-center justify-between gap-2 rounded-md border bg-background p-3 text-sm">
          <span className="flex min-w-0 items-center gap-2">
            <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate" title={adminName ?? undefined}>
              {adminName}
            </span>
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Sign out"
            onClick={signOut}
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </footer>
      </aside>
      <main className="min-w-0 flex-1 overflow-y-auto md:h-dvh">
        <Outlet />
      </main>

      {/* Mobile drawer: same content as the desktop sidebar so behavior matches. */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="left" className="md:hidden">
          <SheetTitle className="sr-only">Dealerships</SheetTitle>
          <header className="flex items-center gap-2 px-5 pt-3">
            <Link
              to="/dealerships"
              onClick={() => setDrawerOpen(false)}
              className="flex items-center gap-2 font-semibold tracking-tight"
            >
              <LumikaLogo theme="auto" size={28} />
              <span>lumika admin</span>
            </Link>
          </header>
          <SheetBody className="flex flex-col gap-3 px-0">
            {drawerBody}
          </SheetBody>
          <footer className="flex items-center justify-between gap-2 border-t bg-background px-5 py-3 text-sm">
            <span className="flex min-w-0 items-center gap-2">
              <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="truncate" title={adminName ?? undefined}>
                {adminName}
              </span>
            </span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Sign out"
              onClick={signOut}
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </footer>
        </SheetContent>
      </Sheet>
    </div>
  );
}
